import { useCallback, useEffect, useRef } from 'react';
import { MDSLoadingIndicator, MDSSnackbar } from '@marqvision/mds-v2';
import { SimpleTextEditor } from '../../SimpleTextEditor';
import { Comment, AppInfo, ListingCommentPanelProps } from '../@types';
import { MessageItem } from './@components/Messages';
import { Styled } from './styles';
import { usePseudoOptimisticUpdate } from './@hooks/usePseudoOptimisticUpdate';

type Props<C extends Comment[]> = {
  listingId: number;
  appInfo: AppInfo;
  apiHook: Omit<ListingCommentPanelProps<C>['apiHook'], 'getReplies'>;
  onOpenThread: (parentComment: Comment) => void;
  onSendSuccess?: () => void;
  onSendError?: () => void;
  onDelete?: () => void;
};
export const CommentsView = <C extends Comment[]>(props: Props<C>) => {
  const { listingId, apiHook, onOpenThread, appInfo } = props;

  const { data, isLoading, isError, isSuccess, isFetching, refetch } = apiHook.getComments(listingId);

  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = scrollableContentRef.current.scrollHeight;
    }
  }, []);

  // note-@jamie: 수동 optimistic update를 위해 임시 데이터를 저장한다
  const { pendingCommentsByType, resolvedComments, addPendingComment, updatePendingComment, clearPendingComments } =
    usePseudoOptimisticUpdate(data);

  const removeCommentMutation = apiHook.removeComment();
  const handleDelete = async (messageId: number) => {
    addPendingComment({ id: messageId } as Comment, 'deleting');
    const isSuccess = await removeCommentMutation.mutateAsync({
      messageId,
      listingId,
    });
    if (isSuccess) {
      updatePendingComment(messageId, 'fetching-after-delete');
      MDSSnackbar({
        title: 'Deleted',
        type: 'complete',
      });
      props.onDelete?.();
      await refetch();
    }
  };

  useEffect(() => {
    // 매 전송마다 밣생
    if (isSuccess && !isFetching) {
      clearPendingComments();
    }
  }, [isSuccess, isFetching, clearPendingComments]);

  useEffect(() => {
    scrollToBottom();
  }, [resolvedComments, scrollToBottom]);

  return (
    <Styled.PanelContent viewMode={'comment'}>
      {isLoading ? (
        <Styled.LoadingStateBox>
          <MDSLoadingIndicator size={48} strokeWidth={4} />
        </Styled.LoadingStateBox>
      ) : isError ? (
        <Styled.LoadingStateBox>{/* 적당한 화면이 필요하다 */}</Styled.LoadingStateBox>
      ) : (
        <>
          <Styled.ScrollableContent ref={scrollableContentRef} viewMode={'comment'}>
            <Styled.CommentContainer>
              {resolvedComments.map((comment) => (
                <MessageItem
                  key={comment.id}
                  comment={comment}
                  appInfo={appInfo}
                  onOpenThread={onOpenThread}
                  onDelete={handleDelete}
                />
              ))}
            </Styled.CommentContainer>
          </Styled.ScrollableContent>
          {!appInfo.isReadOnly && (
            <Styled.FixedTextEditor viewMode={'comment'}>
              <SimpleTextEditor
                appInfo={appInfo}
                apiHook={{
                  getPresignedUrl: apiHook.getPresignedUrl,
                  addComment: apiHook.addComment,
                }}
                isNewMessageLoading={pendingCommentsByType.adding.length > 0}
                listingIds={[listingId]}
                commentId={undefined}
                onSendStart={(newComment: Comment) => {
                  addPendingComment(newComment, 'adding');
                  setTimeout(() => {
                    scrollToBottom();
                  }, 30);
                }}
                onSendSuccess={() => {
                  refetch();
                  props.onSendSuccess?.();
                }}
                onSendError={() => {
                  clearPendingComments();
                  props.onSendError?.();
                }}
              />
            </Styled.FixedTextEditor>
          )}
        </>
      )}
    </Styled.PanelContent>
  );
};
